/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { lazy } from 'react';

import { cssMap as cssMapCompiled } from '@compiled/react';
import { useIntl } from 'react-intl';
import Loadable from 'react-loadable';

import { cssMap, jsx } from '@atlaskit/css';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';

import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';

import messages from './messages';
import { RecordingDisclaimer } from './RecordingDisclaimer';

const styles = cssMap({
	root: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
		paddingInline: token('space.200'),
		paddingBlock: token('space.100'),
	},
	flexContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
	},
	leftMargin: {
		marginLeft: token('space.250'),
	},
	autoMargins: {
		marginLeft: 'auto',
		marginRight: token('space.250'),
	},
});

const stylesCompiled = cssMapCompiled({
	fsCentered: {
		minWidth: 'min(800px, 50vw)',
		marginInline: 'auto',
	},
	fsBottom: { position: 'sticky', bottom: 0 },
	fsTopPad: { paddingTop: token('space.250') },
});

const LocalComponents = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlassian/conversation-assistant/local-frontend-components" */ './local-frontend-components'
		).then((module) => module.LocalFrontendComponents),
	loading: () => null,
});

const LazyLocalComponents = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant/local-frontend-components" */ './local-frontend-components'
	).then((module) => {
		return {
			default: module.LocalFrontendComponents,
		};
	}),
);

function getFinalLocalComponents() {
	if (fg('aimate_conversation_assistant_async_loading')) {
		return React.createElement(Suspense, {
			fallback: null,
			interactionName: 'rovo-conversation-assistant-footer-local-components',
			children: React.createElement(LazyLocalComponents),
		});
	}
	return React.createElement(LocalComponents);
}

type MainFooterProps = {
	isRefreshVersion?: boolean;
};

export const MainFooter = ({ isRefreshVersion = false }: MainFooterProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { fullScreen, isBitbucketBetaEnabled } = useUIConfig();
	const { sendAnalyticsEvent } = useAnalytics();

	const isAdminHubBetaTagEnabled = fg('rovo_chat_beta_tag');

	const shouldShowBetaLozenge = isBitbucketBetaEnabled || isAdminHubBetaTagEnabled;

	const showNewLozenge = fg('rovo_chat_show_new_lozenge');

	const handleContentQualityClick = React.useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'contentQualityLink',
		});
	}, [sendAnalyticsEvent]);

	const isRefreshButNotFullScreen = isRefreshVersion && !fullScreen;
	const { stagingAreaMode } = useStagingAreaState();

	return (
		<div
			css={[
				styles.root,
				fullScreen && stagingAreaMode !== 'inline' && stylesCompiled.fsCentered,
				fullScreen && stylesCompiled.fsBottom,
				fullScreen && stylesCompiled.fsTopPad,
			]}
		>
			<Inline
				spread={isRefreshButNotFullScreen ? undefined : fullScreen ? undefined : 'space-between'}
				alignBlock={'center'}
				alignInline={fullScreen ? 'center' : isRefreshButNotFullScreen ? 'center' : undefined}
			>
				{showNewLozenge ? (
					<Box as="div" xcss={styles.flexContainer}>
						<Inline space="space.050" alignBlock="center">
							<Box xcss={styles.leftMargin}>
								<Stack space="space.050" alignInline="center">
									<Inline space="space.050" alignBlock="center">
										<InformationCircleIcon color="currentColor" label="" size="small" />
										<Link
											appearance="subtle"
											href="https://www.atlassian.com/trust/atlassian-intelligence"
											onClick={handleContentQualityClick}
											target="_blank"
										>
											<Text size="small" color="color.text.subtlest">
												{formatMessage(messages.footerMessage)}
											</Text>
										</Link>
									</Inline>
									<RecordingDisclaimer />
								</Stack>
							</Box>
						</Inline>
						{shouldShowBetaLozenge && (
							<Box xcss={styles.autoMargins}>
								<Lozenge appearance="discovery">{formatMessage(messages.rovoBetaLabel)}</Lozenge>
							</Box>
						)}
					</Box>
				) : (
					<Inline alignBlock="center" space="space.050">
						{shouldShowBetaLozenge && (
							<Lozenge appearance="discovery">{formatMessage(messages.rovoBetaLabel)}</Lozenge>
						)}
						<Stack space="space.050" alignInline="center">
							<Inline alignBlock="center" space="space.050">
								<InformationCircleIcon color="currentColor" label="" size="small" />
								<Link
									appearance="subtle"
									href="https://www.atlassian.com/trust/atlassian-intelligence"
									onClick={handleContentQualityClick}
									target="_blank"
								>
									<Text size="small" color="color.text.subtlest">
										{formatMessage(messages.footerMessage)}
									</Text>
								</Link>
							</Inline>
							<RecordingDisclaimer />
						</Stack>
					</Inline>
				)}
				{fg('rovo_chat_show_local_frontend_components') && getFinalLocalComponents()}
			</Inline>
		</div>
	);
};
